import ClipperLib from 'clipper-lib';
import { TOOLS } from '../constants/drawingTools';
import { calculateDistance } from './drawingUtils';

export const calculateTotalAreaWithHoles = (shapepoints, shapeholes) => {
    if (!shapepoints || shapepoints.length < 6) return 0;

    // Calculate the area of the outer shape using shoelace formula
    let outerArea = 0;
    const numVertices = shapepoints.length / 2;

    for (let i = 0; i < numVertices; i++) {
        const j = (i + 1) % numVertices;
        const xi = shapepoints[i * 2];
        const yi = shapepoints[i * 2 + 1];
        const xj = shapepoints[j * 2];
        const yj = shapepoints[j * 2 + 1];
        outerArea += (xi * yj - xj * yi);
    }
    outerArea = Math.abs(outerArea) / 2;

    // Subtract the areas of all holes
    let totalHoleArea = 0;
    if (shapeholes && Array.isArray(shapeholes)) {
        shapeholes.forEach(hole => {
            if (hole && hole.length >= 6) {
                let holeArea = 0;
                const holeVertices = hole.length / 2;

                for (let i = 0; i < holeVertices; i++) {
                    const j = (i + 1) % holeVertices;
                    const xi = hole[i * 2];
                    const yi = hole[i * 2 + 1];
                    const xj = hole[j * 2];
                    const yj = hole[j * 2 + 1];
                    holeArea += (xi * yj - xj * yi);
                }
                totalHoleArea += Math.abs(holeArea) / 2;
            }
        });
    }

    return Math.max(0, outerArea - totalHoleArea);
};


// Convert polygon points to clipper format
const polygonToClipperPath = (points, shapeX, shapeY) => {
    const path = [];
    for (let i = 0; i < points.length; i += 2) {
        // Convert to absolute coordinates and scale for precision
        const x = Math.round((shapeX + points[i]) * 1000);
        const y = Math.round((shapeY + points[i + 1]) * 1000);
        path.push({ X: x, Y: y });
    }
    return path;
};

// helper function to convert shape with holes to clipper paths
const shapeToClipperPaths = (shape) => {
    const paths = [];

    // Add outer boundary as subject
    const outerPath = polygonToClipperPath(shape.points, shape.x, shape.y);
    paths.push({ path: outerPath, isHole: false });

    // Add holes as clip paths (they subtract from the main shape)
    if (shape.holes && shape.holes.length > 0) {
        shape.holes.forEach(hole => {
            const holePath = polygonToClipperPath(hole, shape.x, shape.y);
            paths.push({ path: holePath, isHole: true });
        });
    }

    return paths;
};

const clipperPathToRelativePoints = (path, originX, originY) => {
    const points = [];
    path.forEach(point => {
        points.push((point.X / 1000) - originX, (point.Y / 1000) - originY);
    });
    return points;
};

const clipperPathCentroid = (path) => {
    let cx = 0, cy = 0;
    path.forEach(point => { cx += point.X; cy += point.Y; });
    return { X: cx / path.length, Y: cy / path.length };
};

// Convert a Clipper solution into one polygon per outer boundary, each with
// its own holes. Returns an array of { points, holes, x, y } (empty when the
// solution has no outer boundaries). Degenerate components (fewer than 3
// vertices or negligible area) are dropped to avoid slivers from integer
// rounding at the ×1000 scale.
const clipperPathsToPolygons = (clipperPaths) => {
    if (!clipperPaths || clipperPaths.length === 0) return [];

    // Separate outer boundaries from holes using ClipperLib's orientation
    const outerPaths = [];
    const holePaths = [];

    clipperPaths.forEach(path => {
        if (ClipperLib.Clipper.Orientation(path)) {
            // Positive orientation = outer boundary
            outerPaths.push(path);
        } else {
            // Negative orientation = hole
            holePaths.push(path);
        }
    });

    if (outerPaths.length === 0) return [];

    // Below this area (px²) a component is a rounding sliver, not real geometry
    const MIN_AREA = 0.5;

    const pathArea = (path) => Math.abs(ClipperLib.Clipper.Area(path)) / 1e6;

    const components = [];
    outerPaths.forEach(outerPath => {
        if (outerPath.length < 3) return;
        const outerArea = pathArea(outerPath);
        if (outerArea < MIN_AREA) return;

        // Assign each hole to the smallest outer that contains its centroid and
        // is larger than the hole. Clipper's EvenOdd output nests strictly
        // (outer > hole > island > island_hole > ...), so a hole can never be
        // contained by an outer smaller than itself — this keeps holes out of
        // nested islands (which would otherwise produce negative-area shapes).
        const componentHoles = holePaths.filter(holePath => {
            if (holePath.length < 3) return false;
            const holeArea = pathArea(holePath);
            if (holeArea < MIN_AREA) return false;
            const holeCentroid = clipperPathCentroid(holePath);
            if (ClipperLib.Clipper.PointInPolygon(holeCentroid, outerPath) !== 1) return false;
            if (holeArea >= outerArea) return false;
            return !outerPaths.some(other =>
                other !== outerPath &&
                pathArea(other) < outerArea &&
                pathArea(other) > holeArea &&
                ClipperLib.Clipper.PointInPolygon(holeCentroid, other) === 1
            );
        });

        const allPaths = [outerPath, ...componentHoles];
        let minX = Infinity, minY = Infinity;
        allPaths.forEach(path => {
            path.forEach(point => {
                const x = point.X / 1000;
                const y = point.Y / 1000;
                if (x < minX) minX = x;
                if (y < minY) minY = y;
            });
        });

        const points = clipperPathToRelativePoints(outerPath, minX, minY);
        const holes = componentHoles.map(holePath => clipperPathToRelativePoints(holePath, minX, minY));

        components.push({
            points,
            holes: holes.length > 0 ? holes : undefined,
            x: minX,
            y: minY
        });
    });

    return components;
};

// Helper function to encapsulate the logic for updating multi-point shapes when a vertex is dragged.
// This removes code duplication between handleVertexDragMove and handleVertexDragEnd.
export const updateMultiPointShapeOnVertexDrag = (shape, vertexIndex, newRelX, newRelY, isHole = false, holeIndex = 0) => {
    if (isHole && shape.holes && shape.holes[holeIndex]) {
        // Update hole vertex
        const updatedHoles = [...shape.holes];
        const updatedHole = [...updatedHoles[holeIndex]];

        if (typeof updatedHole[vertexIndex * 2 + 1] === 'undefined') return shape;

        updatedHole[vertexIndex * 2] = newRelX;
        updatedHole[vertexIndex * 2 + 1] = newRelY;
        updatedHoles[holeIndex] = updatedHole;

        return { ...shape, holes: updatedHoles };
    } else {
        if (shape.type === TOOLS.COTATION && vertexIndex === 2) {
            const [x1, y1, x2, y2] = shape.points;
            const dx = x2 - x1;
            const dy = y2 - y1;
            const L = Math.sqrt(dx * dx + dy * dy);
            if (L > 0) {
                const nx = -dy / L;
                const ny = dx / L;
                const midGeomX = (x1 + x2) / 2;
                const midGeomY = (y1 + y2) / 2;
                const newOffsetDist = (newRelX - midGeomX) * nx + (newRelY - midGeomY) * ny;
                return { ...shape, offsetDist: newOffsetDist };
            }
            return shape;
        }

        // Update main shape vertex (existing logic)
        const updatedPoints = [...shape.points];
        if (typeof updatedPoints[vertexIndex * 2 + 1] === 'undefined') return shape;

        updatedPoints[vertexIndex * 2] = newRelX;
        updatedPoints[vertexIndex * 2 + 1] = newRelY;

        let newSegmentDistances = [];
        if (shape.type === TOOLS.POLYLINE) {
            if (updatedPoints.length >= 4) {
                for (let i = 0; i < updatedPoints.length - 2; i += 2) {
                    newSegmentDistances.push(calculateDistance(
                        updatedPoints[i], updatedPoints[i + 1],
                        updatedPoints[i + 2], updatedPoints[i + 3]
                    ));
                }
            }
        } else if (shape.type === TOOLS.AREA) {
            if (shape.isAxisAlignedRectangle && updatedPoints.length === 8) {
                // Maintain axis-aligned rectangle constraints for the 4 corners:
                // - Vertex i shares X with Vertex 3 - i
                // - Vertex i shares Y with Vertex i ^ 1
                updatedPoints[(3 - vertexIndex) * 2] = newRelX;
                updatedPoints[(vertexIndex ^ 1) * 2 + 1] = newRelY;

                // Re-calculate segment distances for the rectangle sides
                const w = calculateDistance(updatedPoints[0], updatedPoints[1], updatedPoints[2], updatedPoints[3]);
                const h = calculateDistance(updatedPoints[2], updatedPoints[3], updatedPoints[4], updatedPoints[5]);
                newSegmentDistances = [w, h, w, h];
            } else {
                const numVertices = updatedPoints.length / 2;
                newSegmentDistances = [...(shape.segmentDistances || [])];

                // Update distance of the segment ending at the dragged vertex
                const prevVertexIndex = (vertexIndex - 1 + numVertices) % numVertices;
                const pPrevX = updatedPoints[prevVertexIndex * 2];
                const pPrevY = updatedPoints[prevVertexIndex * 2 + 1];
                newSegmentDistances[prevVertexIndex] = calculateDistance(pPrevX, pPrevY, newRelX, newRelY);

                // Update distance of the segment starting at the dragged vertex
                const nextVertexIndex = (vertexIndex + 1) % numVertices;
                const pNextX = updatedPoints[nextVertexIndex * 2];
                const pNextY = updatedPoints[nextVertexIndex * 2 + 1];
                newSegmentDistances[vertexIndex] = calculateDistance(newRelX, newRelY, pNextX, pNextY);
            }
        }

        return { ...shape, points: updatedPoints, segmentDistances: newSegmentDistances };
    }
};

export const isSameVertexEdit = (currentEdit, shapeId, vertexIndex, isHole, holeIndex) => {
    return !!currentEdit &&
        currentEdit.shapeId === shapeId &&
        currentEdit.vertexIndex === vertexIndex &&
        currentEdit.isHole === isHole &&
        currentEdit.holeIndex === holeIndex;
};

// Insert a new vertex into a multi-point shape at the given segment.
// Splices [relX, relY] into the flat `points` array (or a hole's point array
// when isHole is set) between the two vertices defining `segmentIndex`.
// Returns a NEW shape. Main-outline inserts fully recompute segmentDistances
// (never patched in place — the vertex count changes); hole inserts only
// splice the hole array since holes have no segmentDistances.
export const insertVertexIntoShape = (shape, segmentIndex, relX, relY, isHole = false, holeIndex = 0) => {
    if (isHole && shape.holes && shape.holes[holeIndex]) {
        const updatedHoles = [...shape.holes];
        const updatedHole = [...updatedHoles[holeIndex]];
        updatedHole.splice((segmentIndex + 1) * 2, 0, relX, relY);
        updatedHoles[holeIndex] = updatedHole;
        return { ...shape, holes: updatedHoles };
    }

    const points = [...shape.points];
    points.splice((segmentIndex + 1) * 2, 0, relX, relY);

    const numVertices = points.length / 2;
    const segmentDistances = [];
    if (shape.type === TOOLS.AREA) {
        // Closed: distance from each vertex to the next (wrapping around)
        for (let i = 0; i < numVertices; i++) {
            const nextIndex = (i + 1) % numVertices;
            segmentDistances.push(calculateDistance(
                points[i * 2], points[i * 2 + 1],
                points[nextIndex * 2], points[nextIndex * 2 + 1]
            ));
        }
    } else {
        // Open (POLYLINE): distance from each vertex to the next (no closing pair)
        for (let i = 0; i < numVertices - 1; i++) {
            segmentDistances.push(calculateDistance(
                points[i * 2], points[i * 2 + 1],
                points[(i + 1) * 2], points[(i + 1) * 2 + 1]
            ));
        }
    }

    return {
        ...shape,
        points,
        segmentDistances,
        // Rectangle stores 4 explicit corners; inserting a vertex makes it a free polygon
        ...(shape.isAxisAlignedRectangle ? { isAxisAlignedRectangle: false } : {}),
    };
};

// Find the nearest segment of a shape (main outline or any hole) to an
// absolute stage point. Returns { segmentIndex, projectedPoint, distance,
// vertexDistance, isHole, holeIndex } — segmentIndex is the segment's START
// vertex index, projectedPoint is the clamped absolute projection, distance
// is point-to-segment distance, and vertexDistance is the min distance to any
// vertex of the polygon owning the best segment.
export const findNearestSegment = (shape, absX, absY) => {
    const best = {
        segmentIndex: -1,
        projectedPoint: null,
        distance: Infinity,
        vertexDistance: Infinity,
        isHole: false,
        holeIndex: 0,
    };

    // Search one polygon's point array (relative coords), updating `best` on closer segments
    const searchPolygon = (polyPoints, isClosed, isHole, holeIndex) => {
        if (!polyPoints || polyPoints.length < 4) return;
        const numVertices = polyPoints.length / 2;

        let polygonVertexDistance = Infinity;
        for (let i = 0; i < numVertices; i++) {
            const vx = shape.x + polyPoints[i * 2];
            const vy = shape.y + polyPoints[i * 2 + 1];
            const d = Math.sqrt(Math.pow(absX - vx, 2) + Math.pow(absY - vy, 2));
            if (d < polygonVertexDistance) polygonVertexDistance = d;
        }

        const numSegments = isClosed ? numVertices : numVertices - 1;
        for (let i = 0; i < numSegments; i++) {
            const j = (i + 1) % numVertices;
            const ax = shape.x + polyPoints[i * 2];
            const ay = shape.y + polyPoints[i * 2 + 1];
            const bx = shape.x + polyPoints[j * 2];
            const by = shape.y + polyPoints[j * 2 + 1];

            const dx = bx - ax;
            const dy = by - ay;
            const lenSq = dx * dx + dy * dy;
            let t = lenSq > 0 ? ((absX - ax) * dx + (absY - ay) * dy) / lenSq : 0;
            t = Math.max(0, Math.min(1, t));
            const projX = ax + t * dx;
            const projY = ay + t * dy;
            const dist = Math.sqrt(Math.pow(absX - projX, 2) + Math.pow(absY - projY, 2));

            if (dist < best.distance) {
                best.distance = dist;
                best.segmentIndex = i;
                best.projectedPoint = { x: projX, y: projY };
                best.isHole = isHole;
                best.holeIndex = holeIndex;
                best.vertexDistance = polygonVertexDistance;
            }
        }
    };

    // Main outline: closed for AREA, open for POLYLINE
    searchPolygon(shape.points, shape.type === TOOLS.AREA, false, 0);

    // Holes are closed polygons
    if (shape.holes && Array.isArray(shape.holes)) {
        shape.holes.forEach((hole, index) => searchPolygon(hole, true, true, index));
    }

    return best;
};

// Perform a boolean operation between one or more polygons (polygon1) and a
// single polygon (polygon2). polygon1 may be a single polygon or an array of
// polygons — the array form is used by the fold in executeBooleanOperation,
// where the accumulator components are guaranteed disjoint (Clipper output),
// so EvenOdd fill among multiple subject paths causes no parity cancellation.
// Every polygon1 path (outers and holes) is a subject and every polygon2 path
// is a clip, for all operations. Returns an array of { points, holes, x, y }
// (one per outer component), or null when the operation fails or produces no
// geometry.
export const performBooleanOperation = (polygon1, polygon2, operation) => {
    try {
        const clipper = new ClipperLib.Clipper();

        const shapes1 = Array.isArray(polygon1) ? polygon1 : [polygon1];

        shapes1.forEach(shape => {
            shapeToClipperPaths(shape).forEach(({ path }) => {
                clipper.AddPath(path, ClipperLib.PolyType.ptSubject, true);
            });
        });
        shapeToClipperPaths(polygon2).forEach(({ path }) => {
            clipper.AddPath(path, ClipperLib.PolyType.ptClip, true);
        });

        const solution = new ClipperLib.Paths();
        let success = false;

        // Perform the specified operation
        switch (operation) {
            case 'union':
                success = clipper.Execute(ClipperLib.ClipType.ctUnion, solution, 
                    ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftEvenOdd);
                break;
            case 'intersection':
                success = clipper.Execute(ClipperLib.ClipType.ctIntersection, solution,
                    ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftEvenOdd);
                break;
            case 'difference':
                success = clipper.Execute(ClipperLib.ClipType.ctDifference, solution,
                    ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftEvenOdd);
                break;
            case 'xor':
                success = clipper.Execute(ClipperLib.ClipType.ctXor, solution,
                    ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftEvenOdd);
                break;
            default:
                throw new Error(`Unknown operation: ${operation}`);
        }

        if (!success || solution.length === 0) {
            return null;
        }

        return clipperPathsToPolygons(solution);

    } catch (error) {
        console.error('Boolean operation failed:', error);
        return null;
    }
};

// Calculate perimeter and segment distances for the resulting area
export const calculateAreaMeasurements = (points) => {
    const segmentDistances = [];
    const numVertices = points.length / 2;

    for (let i = 0; i < numVertices; i++) {
        const p1x = points[i * 2];
        const p1y = points[i * 2 + 1];
        const p2Index = (i + 1) % numVertices;
        const p2x = points[p2Index * 2];
        const p2y = points[p2Index * 2 + 1];

        const distance = Math.sqrt(Math.pow(p2x - p1x, 2) + Math.pow(p2y - p1y, 2));
        segmentDistances.push(distance);
    }

    return segmentDistances;
};